import { Link, useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import {
  FileText,
  FolderOpen,
  CheckCircle2,
  Clock,
  ArrowRight,
  Phone,
  Mail,
  Inbox,
  ChevronLeft,
  Lock,
} from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { useMatter, type MatterSummary } from '../hooks/useMatter';
import { supabase } from '../lib/supabase';
import { SUPPLEMENTARY_FORM_CODES_SQL } from '../lib/formCodes';
import StaffDashboard from './StaffDashboard';
import LifecycleTimeline from '../components/LifecycleTimeline';
import { useMatterTimeline } from '../lib/useMatterTimeline';
import { pickFormId, loadSchema, formHasStage2, hasSponsorForm, sponsorRoleNoun } from '../lib/schema-runtime/loadSchema';
import { rowsToFormState } from '../lib/schema-runtime/formStateAdapter';
import { computeOwnFieldProgress } from '../lib/schema-runtime/validateForm';

interface IntakeFormSummary {
  id: string;
  status: 'in_progress' | 'completed' | 'submitted' | 'reopened';
  last_saved_at: string;
  completed_at: string | null;
  submitted_at: string | null;
  stage1_completed_at: string | null;
}

interface VerifiedReceipt {
  id: string;
  ai_extracted_date: string | null;
  ai_extracted_amount: number | null;
  ai_extracted_currency: string | null;
  ai_extracted_method: string | null;
  manual_override: { date?: string; amount?: number; currency?: string; method?: string } | null;
}

export default function Dashboard() {
  const { user } = useAuth();
  const params = useParams<{ id?: string }>();
  const {
    loading,
    error,
    matters,
    activeMatter,
    contact,
    caseManager,
    isStaff,
    actuallyStaff,
    impersonatedMatterId,
    impersonatedRole,
    effectiveContactId,
    setActiveMatterId,
  } = useMatter();

  // When impersonating, fetch the impersonated client's contact so we can
  // display their name + their applicant context (rather than the staff user's).
  const [impersonatedContact, setImpersonatedContact] = useState<{
    given_names: string | null;
    family_name: string | null;
    contact_type: string;
    primary_contact: { given_names: string | null; family_name: string | null } | null;
  } | null>(null);
  useEffect(() => {
    let cancelled = false;
    if (!actuallyStaff || !impersonatedMatterId) {
      setImpersonatedContact(null);
      return;
    }
    (async () => {
      // Pull both contacts on the matter — we'll pick the right one based on role.
      const { data } = await supabase
        .from('matters')
        .select(
          `id,
           primary_contact:contacts!matters_primary_contact_id_fkey (given_names, family_name, contact_type),
           sponsor_contact:contacts!matters_sponsor_contact_id_fkey (given_names, family_name, contact_type)`,
        )
        .eq('id', impersonatedMatterId)
        .maybeSingle();
      if (cancelled || !data) return;
      const row = data as {
        primary_contact: { given_names: string | null; family_name: string | null; contact_type: string } | null;
        sponsor_contact: { given_names: string | null; family_name: string | null; contact_type: string } | null;
      };
      const target = impersonatedRole === 'sponsor' ? row.sponsor_contact : row.primary_contact;
      setImpersonatedContact(
        target
          ? {
              ...target,
              // For sponsor view, surface the applicant's name as context for the welcome blurb.
              primary_contact: impersonatedRole === 'sponsor' ? row.primary_contact : null,
            }
          : null,
      );
    })();
    return () => {
      cancelled = true;
    };
  }, [actuallyStaff, impersonatedMatterId, impersonatedRole]);

  const isMatterDetailRoute = Boolean(params.id);

  // When the URL specifies a matter ID, sync it to active.
  useEffect(() => {
    if (params.id && params.id !== activeMatter?.id) {
      setActiveMatterId(params.id);
    }
  }, [params.id, activeMatter?.id, setActiveMatterId]);

  if (loading) {
    return <div className="text-sm text-slate-500">Loading your matter...</div>;
  }

  if (error) {
    return (
      <div className="card p-6 text-sm text-red-600">
        Couldn't load your matter: {error}
      </div>
    );
  }

  // Staff get a different home page entirely. The matter-detail route
  // (/matters/:id) still falls through to the per-matter view below for
  // backwards compat until MatterDetail.tsx replaces it.
  if (isStaff && !isMatterDetailRoute) {
    return <StaffDashboard />;
  }

  if (matters.length === 0) {
    return <EmptyState email={user?.email ?? ''} />;
  }

  if (matters.length > 1 && !activeMatter) {
    return <MatterPicker matters={matters} onPick={setActiveMatterId} />;
  }

  return (
    <div className="space-y-6">
      {isMatterDetailRoute && isStaff && activeMatter && (
        <div className="flex items-center gap-2 text-sm">
          <Link to="/matters" className="text-slate-500 hover:text-[#0B2341] inline-flex items-center gap-1">
            <ChevronLeft size={14} /> All matters
          </Link>
          <span className="text-slate-300">/</span>
          <span className="font-medium text-[#0B2341]">
            {activeMatter.visa_subclass}
            {activeMatter.variant ? ` ${activeMatter.variant}` : ''} matter
          </span>
        </div>
      )}
      {/* Per Tzvi 2026-05-07: clients (applicants AND sponsors) shouldn't see
          a Switch matter UI on their dashboard. They access the portal in the
          context of one matter at a time; staff direct them if they have more
          than one. The previous Switch matter button is gone — left no text
          here so the UI doesn't surface anything matter-list-shaped to clients. */}
      {!isMatterDetailRoute && isStaff && (
        <div className="flex items-center justify-between text-xs text-slate-500">
          <span>You're viewing as staff. Use the Matters tab to navigate between client matters.</span>
          <Link to="/matters" className="text-[#2CABEA] hover:underline">
            Open Matters
          </Link>
        </div>
      )}

      <DashboardForMatter
        matter={activeMatter!}
        firstName={
          impersonatedContact?.given_names ??
          contact?.given_names ??
          user?.email?.split('@')[0] ??
          ''
        }
        caseManager={caseManager}
        contactType={resolveDashboardRole(
          impersonatedMatterId,
          impersonatedRole,
          impersonatedContact?.contact_type ?? contact?.contact_type ?? null,
        )}
        intakeContactId={resolveIntakeContactId(isStaff, activeMatter?.primary_contact_id ?? null, effectiveContactId)}
        applicantName={
          impersonatedContact?.primary_contact
            ? [impersonatedContact.primary_contact.given_names, impersonatedContact.primary_contact.family_name]
                .filter(Boolean)
                .join(' ')
                .trim() || null
            : null
        }
      />
    </div>
  );
}

function DashboardForMatter({
  matter,
  firstName,
  caseManager,
  contactType,
  applicantName,
  intakeContactId,
}: {
  matter: MatterSummary;
  firstName: string;
  caseManager: ReturnType<typeof useMatter>['caseManager'];
  contactType: string;
  applicantName: string | null;
  intakeContactId: string | null;
}) {
  const [intake, setIntake] = useState<IntakeFormSummary | null>(null);
  const [stage1Pct, setStage1Pct] = useState<number>(0);
  const [stage2Pct, setStage2Pct] = useState<number>(0);
  // Single-stage visas (600, 461, BVB) have no post-lodgement form at all.
  const [hasStage2, setHasStage2] = useState<boolean>(false);
  const [verifiedReceipts, setVerifiedReceipts] = useState<VerifiedReceipt[]>([]);

  const isSponsor = contactType === 'sponsor';

  useEffect(() => {
    let cancelled = false;
    async function load() {
      // BUG-7: read the SAME intake_forms row the intake page reads, resolved
      // via the same contact id (intakeContactId, mirroring IntakeFormV2's
      // staffViewingClientForm ? primary_contact_id : effectiveContactId). Never
      // run this without a contact filter: an unfiltered query returns the
      // latest form on the matter by ANY contact, so the tile could show the
      // applicant's progress (or, for staff filtering by their own contact,
      // "Not started 0%") while the intake page shows the real client %. When
      // the contact is not yet resolved, show nothing rather than a wrong form.
      if (!intakeContactId) {
        const twoStageOnly = await formHasStage2(matter.visa_subclass, isSponsor, matter.variant);
        if (cancelled) return;
        setIntake(null);
        setStage1Pct(0);
        setStage2Pct(0);
        setHasStage2(twoStageOnly);
        return;
      }
      const query = supabase
        .from('intake_forms')
        .select('id, status, last_saved_at, completed_at, submitted_at, stage1_completed_at')
        .eq('matter_id', matter.id)
        .eq('contact_id', intakeContactId)
        .not('form_code', 'in', SUPPLEMENTARY_FORM_CODES_SQL)
        .order('last_saved_at', { ascending: false })
        .limit(1);
      const { data: forms } = await query;
      if (cancelled) return;
      const form = ((forms ?? [])[0] as IntakeFormSummary | undefined) ?? null;
      setIntake(form);

      const twoStage = await formHasStage2(matter.visa_subclass, isSponsor, matter.variant);
      if (!cancelled) setHasStage2(twoStage);

      if (form) {
        const { data: fields } = await supabase
          .from('intake_field_data')
          .select('field_key, field_value')
          .eq('intake_form_id', form.id);
        if (cancelled) return;
        const [s1, s2] = await Promise.all([
          computeCompletion(matter.visa_subclass, isSponsor, matter.variant, fields ?? [], 1),
          computeCompletion(matter.visa_subclass, isSponsor, matter.variant, fields ?? [], 2),
        ]);
        if (!cancelled) {
          setStage1Pct(s1);
          setStage2Pct(s2);
        }
      } else {
        setStage1Pct(0);
        setStage2Pct(0);
      }

      // Document counts (B1b/B12 fix): this used to run its own separate
      // `documents` COUNT query here — a raw row count of every non-deleted
      // document, matched against a required-doc total computed by a
      // SEPARATE checklistForVisa() call. That numerator counted ALL
      // uploads (including optional/ad-hoc ones, and — critically — old
      // superseded versions of a replaced file, which stay non-deleted)
      // while the denominator counted only DISTINCT REQUIRED checklist
      // items, an apples-to-oranges comparison against a differently-
      // filtered list than what useMatterTimeline computes for the SAME
      // matter (the "matter overview document counter off-by-one" B1b
      // report — "15 of 18" vs a real checklist total of 16). Now sourced
      // entirely from useMatterTimeline's ctx below (ctx.requiredDocsUploaded
      // / ctx.requiredDocsTotal) — the same single computation MatterOverview
      // and LodgementGate already use, so this card can no longer disagree
      // with them.
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [matter.id, isSponsor, matter.visa_subclass, matter.variant, intakeContactId]);

  // Verified payment receipts — read-only confirmation for both applicant
  // and sponsor (decision A, 2026-05-07). Column projection enforces that
  // we never read file_path / ai_raw_response / ai_cost_usd over the wire.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from('payment_receipts' as never)
        .select('id, ai_extracted_date, ai_extracted_amount, ai_extracted_currency, ai_extracted_method, manual_override')
        .eq('matter_id', matter.id)
        .eq('status', 'verified')
        .order('ai_extracted_date', { ascending: false, nullsFirst: false });
      if (cancelled) return;
      setVerifiedReceipts((data ?? []) as unknown as VerifiedReceipt[]);
    })();
    return () => {
      cancelled = true;
    };
  }, [matter.id]);

  // Unified timeline: same context-driven steps the staff LodgementGate
  // and MatterOverview use, just rendered with the client-friendly labels.
  // Replaces the legacy hardcoded buildTimeline()/Submitted-header pair so
  // the dashboard can never lag behind the actual matter status.
  const { steps: unifiedSteps, ctx: timelineCtx } = useMatterTimeline({
    matterId: matter.id,
    visaSubclass: matter.visa_subclass,
    variant: matter.variant,
    applicantContactId: matter.primary_contact_id,
    sponsorContactId: matter.sponsor_contact_id ?? null,
  });
  // Post-lodgement unlocks on the server-verified stage-1 latch, not a live %
  // re-compute. A latched stage always reads Complete / 100% (never backwards).
  const stage1Latched = intake?.stage1_completed_at != null;
  const stage1Display = stage1Latched ? 100 : stage1Pct;

  // The next-step card + its progress bar are about the PRE-LODGEMENT (stage-1)
  // form, so they track stage-1 progress (stage1Display) — not a blended
  // all-stages number that also counts the still-locked post-lodgement form and
  // so could never reach 100 pre-lodgement (which would strand the applicant on
  // "continue pre-lodgement" forever).
  // 461/600-tourist/020 have a "sponsor" contact (NZ citizen family member,
  // supporter, etc.) but NO separate sponsor form — their details are
  // captured directly on the applicant's own form (SponsorPanel.tsx,
  // pickFormId, IntakeFormV2 all already gate on this). The dashboard's
  // next-action card and welcome banner used to be the one place that
  // didn't consult it, so a family-member contact on one of these visas saw
  // "Fill in your sponsor form" / "Start sponsor form" pointing at a form
  // that immediately no-ops with "There is no separate sponsor form for
  // this visa" (B12-F3 / B12 UAT-13-F3, reproduced on both 461 test
  // matters). Route both copy sites through the same helper the intake uses.
  const sponsorHasOwnForm = hasSponsorForm(matter.visa_subclass);
  const next = nextStep({
    isSponsor,
    sponsorHasOwnForm,
    sponsorRole: sponsorRoleNoun(matter.visa_subclass),
    intakeStatus: intake?.status ?? null,
    completion: stage1Display,
    // B1b/B12 fix: sourced from useMatterTimeline's ctx (see the comment
    // above the removed standalone doc-count fetch) so this card can't
    // disagree with MatterOverview/LodgementGate's own document counts.
    docCount: timelineCtx?.requiredDocsUploaded ?? 0,
    requiredDocCount: timelineCtx?.requiredDocsTotal ?? 0,
  });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold mb-1">Welcome, {firstName}.</h1>
        <p className="text-slate-500">
          {isSponsor
            ? sponsorWelcomeBlurb(matter.visa_subclass, matter.variant, applicantName, sponsorHasOwnForm)
            : `${visaWelcomeBlurb(matter.visa_subclass, matter.variant)} You have two things to do, both at your own pace. Save as you go.`}
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main column */}
        <div className="lg:col-span-2 space-y-6">
          {/* Intake forms. Only the partner visas are two-stage — single-stage
              visas (600, 461, BVB) have no post-lodgement form, so promising one
              would be a form that never unlocks. */}
          <div className="card">
            <div className="section">
              <h2 className="text-lg font-bold">{hasStage2 ? 'Your intake forms' : 'Your intake form'}</h2>
              <p className="text-sm text-slate-500 mt-1">
                {hasStage2
                  ? 'Complete the pre-lodgement form first. The post-lodgement form opens once that is done.'
                  : 'Fill this in at your own pace. It saves as you go.'}
              </p>
            </div>
            <div className={`section grid grid-cols-1 gap-4 ${hasStage2 ? 'sm:grid-cols-2' : ''}`}>
              {/* Pre-lodgement */}
              <Link to="/intake" className="block rounded-xl border border-slate-200 hover:border-[#2CABEA] p-4 transition-colors">
                <div className="flex items-center gap-2 text-[#0B2341] font-semibold">
                  <FileText size={16} className="text-[#2CABEA]" /> {hasStage2 ? 'Pre-lodgement intake' : 'Intake form'}
                </div>
                <div className="mt-3 flex items-center justify-between text-sm">
                  <span className="text-slate-500">{stage1Display === 100 ? 'Complete' : stage1Display === 0 ? 'Not started' : 'In progress'}</span>
                  <span className="font-semibold">{stage1Display}%</span>
                </div>
                <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden mt-2">
                  <div className="h-full bg-[#2CABEA] transition-all" style={{ width: `${stage1Display}%` }} />
                </div>
              </Link>
              {/* Post-lodgement — only for genuinely two-stage visas */}
              {hasStage2 && (stage1Latched ? (
                <Link to="/intake?stage=2" className="block rounded-xl border border-slate-200 hover:border-[#2CABEA] p-4 transition-colors">
                  <div className="flex items-center gap-2 text-[#0B2341] font-semibold">
                    <FileText size={16} className="text-[#2CABEA]" /> Post-lodgement intake
                  </div>
                  <div className="mt-3 flex items-center justify-between text-sm">
                    <span className="text-slate-500">{stage2Pct === 100 ? 'Complete' : stage2Pct === 0 ? 'Not started' : 'In progress'}</span>
                    <span className="font-semibold">{stage2Pct}%</span>
                  </div>
                  <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden mt-2">
                    <div className="h-full bg-[#2CABEA] transition-all" style={{ width: `${stage2Pct}%` }} />
                  </div>
                </Link>
              ) : (
                <div className="block rounded-xl border border-dashed border-slate-200 p-4 bg-slate-50/60">
                  <div className="flex items-center gap-2 text-slate-400 font-semibold">
                    <Lock size={16} /> Post-lodgement intake
                  </div>
                  <p className="mt-3 text-sm text-slate-400">
                    Unlocks after you complete the pre-lodgement form.
                  </p>
                </div>
              ))}
            </div>
          </div>

          <div className="card">
            <div className="section flex items-start justify-between">
              <div>
                <div className="text-xs font-semibold text-[#2CABEA] uppercase tracking-wide mb-1">
                  {next.eyebrow}
                </div>
                <h2 className="text-lg font-bold">{next.heading}</h2>
                <p className="text-sm text-slate-500 mt-1">
                  {next.description}
                </p>
              </div>
              {next.statusChip && (
                <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${next.statusChip.tone}`}>
                  {next.statusChip.icon} {next.statusChip.label}
                </span>
              )}
            </div>
            {next.showProgress && (
              <div className="section">
                <div className="flex items-center justify-between mb-2 text-sm">
                  <span className="font-medium text-slate-700">Progress</span>
                  <span className="text-slate-500">{stage1Display}% complete</span>
                </div>
                <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-[#2CABEA] transition-all"
                    style={{ width: `${stage1Display}%` }}
                  />
                </div>
              </div>
            )}
            <div className="section flex flex-wrap gap-3">
              {next.primaryCta && (
                <Link to={next.primaryCta.to} className="btn-primary">
                  {next.primaryCta.label} <ArrowRight size={16} />
                </Link>
              )}
              {next.secondaryCta && (
                <Link to={next.secondaryCta.to} className="btn-secondary">
                  <FolderOpen size={16} /> {next.secondaryCta.label}
                </Link>
              )}
            </div>
          </div>

          {verifiedReceipts.length > 0 && (
            <div className="card">
              <div className="section">
                <h2 className="text-lg font-bold">Payments received</h2>
                <p className="text-sm text-slate-500 mt-1">
                  Confirmed receipts on your matter.
                </p>
              </div>
              <div className="section pt-0">
                <ul className="space-y-2 text-sm">
                  {verifiedReceipts.map((r) => (
                    <li
                      key={r.id}
                      className="flex items-center gap-2 text-slate-700"
                    >
                      <CheckCircle2 size={14} className="text-green-600 shrink-0" />
                      <span>{formatReceiptLine(r)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          <div className="card">
            <div className="section">
              <h2 className="text-lg font-bold">Your application timeline</h2>
              <p className="text-sm text-slate-500 mt-1">
                What we've done and what's coming up.
              </p>
            </div>
            <div className="section">
              <LifecycleTimeline steps={unifiedSteps} mode="client" />
            </div>
          </div>
        </div>

        {/* Side column */}
        <div className="space-y-6">
          <div className="card p-6">
            <h3 className="font-bold mb-4">Your matter</h3>
            <dl className="space-y-3 text-sm">
              <div>
                <dt className="text-slate-500">Matter ID</dt>
                <dd className="font-mono font-semibold">{matter.matter_id ?? '-'}</dd>
              </div>
              <div>
                <dt className="text-slate-500">Visa subclass</dt>
                <dd className="font-semibold">
                  {matter.visa_subclass}
                  {matter.variant ? ` - ${matter.variant}` : ''}
                </dd>
              </div>
              <div>
                <dt className="text-slate-500">Status</dt>
                <dd className="font-semibold capitalize">{matter.status.replace(/_/g, ' ')}</dd>
              </div>
              {matter.initial_payment_date && !isSponsor && (
                <div>
                  <dt className="text-slate-500">Initial payment</dt>
                  <dd className="font-semibold text-green-600">
                    Received {formatDate(matter.initial_payment_date)}
                  </dd>
                </div>
              )}
            </dl>
          </div>

          {caseManager && (
            <div className="card p-6">
              <h3 className="font-bold mb-4">Your case manager</h3>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-full bg-[#EBF7FD] text-[#1A8DC7] grid place-items-center font-bold">
                  {(caseManager.given_names ?? '?')[0]}
                  {(caseManager.family_name ?? '?')[0]}
                </div>
                <div>
                  <div className="font-semibold">
                    {caseManager.given_names} {caseManager.family_name}
                  </div>
                  <div className="text-xs text-slate-500">
                    {caseManager.staff_role
                      ? caseManager.staff_role.charAt(0).toUpperCase() + caseManager.staff_role.slice(1)
                      : 'Case manager'}
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                {caseManager.email && (
                  <a
                    href={`mailto:${caseManager.email}`}
                    className="flex items-center gap-2 text-sm text-slate-700 hover:text-[#2CABEA]"
                  >
                    <Mail size={14} /> {caseManager.email}
                  </a>
                )}
                {caseManager.phone && (
                  <a
                    href={`tel:${caseManager.phone}`}
                    className="flex items-center gap-2 text-sm text-slate-700 hover:text-[#2CABEA]"
                  >
                    <Phone size={14} /> {caseManager.phone}
                  </a>
                )}
              </div>
              <div className="mt-4 pt-4 border-t border-slate-100">
                <p className="text-xs text-slate-500">
                  Response time: within 24 to 48 business hours (Melbourne time).
                </p>
              </div>
            </div>
          )}

          <div className="card p-6">
            <h3 className="font-bold mb-3">Helpful info</h3>
            <ul className="space-y-2 text-sm">
              {dashboardHelpLinks(matter.visa_subclass).map((link) => (
                <li key={link.label}>
                  {link.to ? (
                    <Link to={link.to} className="text-[#2CABEA] hover:underline flex items-center gap-1.5">
                      <FileText size={14} /> {link.label}
                    </Link>
                  ) : (
                    <a
                      href={link.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#2CABEA] hover:underline flex items-center gap-1.5"
                    >
                      <ArrowRight size={14} /> {link.label}
                    </a>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

function MatterPicker({
  matters,
  onPick,
}: {
  matters: MatterSummary[];
  onPick: (id: string) => void;
}) {
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold mb-1">Choose a matter</h1>
        <p className="text-slate-500">You have multiple matters with ADI. Pick one to continue.</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {matters.map((m) => (
          <button
            key={m.id}
            onClick={() => onPick(m.id)}
            className="card p-6 text-left hover:border-[#2CABEA] hover:shadow-md transition-all"
          >
            <div className="text-xs font-mono text-slate-500 mb-1">{m.matter_id ?? m.id.slice(0, 8)}</div>
            <div className="font-semibold mb-1">
              {m.visa_subclass}
              {m.variant ? ` - ${m.variant}` : ''}
            </div>
            <div className="text-xs text-slate-500 capitalize">{m.status.replace(/_/g, ' ')}</div>
          </button>
        ))}
      </div>
    </div>
  );
}

function EmptyState({ email }: { email: string }) {
  return (
    <div className="max-w-2xl mx-auto">
      <div className="card p-10 text-center">
        <div className="w-14 h-14 mx-auto rounded-full bg-slate-100 text-slate-400 grid place-items-center mb-3">
          <Inbox size={24} />
        </div>
        <h2 className="font-bold mb-2">Your case is being set up</h2>
        <p className="text-sm text-slate-500 max-w-md mx-auto">
          We'll email you at <span className="font-medium text-slate-700">{email}</span> as soon as
          your intake form is ready. If this takes longer than a business day, please reach out at{' '}
          <a className="text-[#2CABEA] hover:underline" href="mailto:adi@adibusinessgroup.com">
            adi@adibusinessgroup.com
          </a>
          .
        </p>
      </div>
    </div>
  );
}

// --- helpers ---------------------------------------------------------------

// Exported (in addition to being used internally) so a guard test can pin
// the B12-F3 fix: a contact on a no-sponsor-form visa (461/020/600-tourist)
// must never see "sponsor form" wording or a CTA into it, regardless of
// what changes elsewhere in this file. See __tests__/dashboardNextStep.test.ts.
export function sponsorWelcomeBlurb(
  visaSubclass: string,
  variant: string | null,
  applicantName: string | null,
  hasOwnForm: boolean,
): string {
  const visa = `${visaSubclass}${variant ? ` ${variant}` : ''}`;
  // Vocabulary: "inviter" on the 600 tourist stream, "sponsor" everywhere else.
  const role = sponsorRoleNoun(visaSubclass);
  if (!hasOwnForm) {
    // No separate form for this visa type (461, 020, 600-tourist) — their
    // details are captured on the applicant's own form. Never promise a
    // "sponsor form" that doesn't exist.
    if (applicantName) {
      return `You've been added as a contact on ${applicantName}'s ${visa} application. There's no separate form for you to fill in. Your details are captured directly on their application.`;
    }
    return `You've been added as a contact on a ${visa} application. There's no separate form for you to fill in. Your details are captured directly on the application.`;
  }
  if (applicantName) {
    return `You've been added as the ${role} on ${applicantName}'s ${visa} application. Your task is to fill in your ${role} form so we can lodge.`;
  }
  return `You've been added as the ${role} on a ${visa} application. Your task is to fill in your ${role} form so we can lodge.`;
}

/**
 * BUG-6: resolve the dashboard role used to pick the next-step card.
 *
 * While staff preview as a client, the impersonated role is known
 * synchronously (impersonatedRole, from session state) but the impersonated
 * contact's own record loads async. The old code defaulted to 'applicant'
 * until that fetch resolved, so a sponsor preview flashed the applicant
 * pre-lodgement card for ~0.5s before flipping. Prefer the synchronous role
 * when impersonating so the correct card renders on the first paint.
 */
export interface DashboardHelpLink {
  label: string;
  /** Internal route (rendered as a router Link). */
  to?: string;
  /** External URL (rendered as an anchor, new tab). */
  href?: string;
}

/**
 * BUG-8: the dashboard "Helpful info" links must match the matter's visa.
 * A 600-visitor client was shown "Partner visa pathway overview" and the AFP
 * police-check link, neither of which applies to a tourist visa. Only the
 * partner-visa subclasses (which require the partner pathway context and an
 * Australian police check) get those links; everyone gets the intake help.
 */
export function dashboardHelpLinks(visaSubclass: string): DashboardHelpLink[] {
  const PARTNER_VISAS = ['820', '801', '309', '100', '300'];
  const links: DashboardHelpLink[] = [{ label: 'The intake form explained', to: '/intake' }];
  if (PARTNER_VISAS.includes(visaSubclass)) {
    links.push({ label: 'Partner visa pathway overview', href: 'https://adiimmigration.com.au' });
    links.push({
      label: 'AFP police check (apply here)',
      href: 'https://afpnationalpolicechecks.converga.com.au/',
    });
  }
  return links;
}

/**
 * BUG-7: resolve which contact's intake_forms row the dashboard tile should
 * read, mirroring IntakeFormV2 exactly so the tile % and the intake-page %
 * are computed over the same form and can never disagree.
 *
 * - Staff viewing a matter without impersonating (isStaff) read the
 *   applicant's (primary) form, the same row the intake page shows them.
 * - Everyone else (real clients, and staff impersonating a client) read the
 *   form owned by their effective contact.
 */
export function resolveIntakeContactId(
  isStaff: boolean,
  primaryContactId: string | null,
  effectiveContactId: string | null,
): string | null {
  return isStaff ? primaryContactId : effectiveContactId;
}

export function resolveDashboardRole(
  impersonatedMatterId: string | null,
  impersonatedRole: 'applicant' | 'sponsor',
  contactType: string | null,
): string {
  if (impersonatedMatterId) return impersonatedRole;
  return contactType ?? 'applicant';
}

export function nextStep({
  isSponsor,
  sponsorHasOwnForm,
  sponsorRole = 'sponsor',
  intakeStatus,
  completion,
  docCount,
  requiredDocCount,
}: {
  isSponsor: boolean;
  sponsorHasOwnForm: boolean;
  /**
   * Client-facing noun for the second party: 'inviter' on the 600 tourist
   * stream, 'sponsor' everywhere else. Defaults to 'sponsor' so every existing
   * caller and copy string is unchanged.
   */
  sponsorRole?: 'sponsor' | 'inviter';
  intakeStatus: string | null;
  completion: number;
  docCount: number;
  requiredDocCount: number;
}): {
  eyebrow: string;
  heading: string;
  description: string;
  showProgress: boolean;
  primaryCta: { label: string; to: string } | null;
  secondaryCta: { label: string; to: string } | null;
  statusChip: { tone: string; label: string; icon: React.ReactNode } | null;
} {
  // A contact on a visa with no separate sponsor form (461 NZ citizen family
  // member, 020 BVB, 600-tourist) — never advertise a "sponsor form" CTA
  // that /intake immediately no-ops on (B12-F3). Nothing for them to do here.
  if (isSponsor && !sponsorHasOwnForm) {
    return {
      eyebrow: 'Your role on this application',
      heading: 'No form needed from you',
      description:
        "This visa type doesn't have a separate form for you to complete. Your details are captured directly on the applicant's own form. There's nothing further you need to do right now.",
      showProgress: false,
      primaryCta: null,
      secondaryCta: null,
      statusChip: null,
    };
  }

  // Second-party flow has its own short-form journey.
  if (isSponsor) {
    const isInviter = sponsorRole === 'inviter';
    if (!intakeStatus || intakeStatus === 'in_progress') {
      return {
        eyebrow: 'Your next step',
        heading:
          completion === 0 ? `Fill in your ${sponsorRole} form` : `Continue your ${sponsorRole} form`,
        description: isInviter
          ? 'A short form: your details, how you know the visitor, your status in Australia, and what support you are providing. About 5 minutes.'
          : 'A few quick sections on your relationship to the applicant, your background, and your eligibility to sponsor. About 15 minutes.',
        showProgress: completion > 0,
        primaryCta: {
          label: completion === 0 ? `Start ${sponsorRole} form` : 'Continue',
          to: '/intake',
        },
        secondaryCta: null,
        statusChip: completion > 0
          ? { tone: 'bg-[#FB8E28]/10 text-[#D97706]', label: 'In progress', icon: <Clock size={12} /> }
          : null,
      };
    }
    if (intakeStatus === 'completed' || intakeStatus === 'submitted') {
      return {
        eyebrow: 'All done',
        heading: isInviter ? 'Inviter form submitted' : 'Sponsor form submitted',
        description: 'Thank you. Your case manager will be in touch if anything else is needed.',
        showProgress: false,
        primaryCta: null,
        secondaryCta: null,
        statusChip: { tone: 'bg-green-100 text-green-700', label: 'Submitted', icon: <CheckCircle2 size={12} /> },
      };
    }
  }

  // Applicant flow.
  if (!intakeStatus || (intakeStatus === 'in_progress' && completion < 100)) {
    return {
      eyebrow: 'Your next step',
      heading: completion === 0 ? 'Start your pre-lodgement form' : 'Continue your pre-lodgement form',
      description:
        'About 45 to 90 minutes. Save as you go and come back any time.',
      showProgress: true,
      primaryCta: { label: completion === 0 ? 'Start pre-lodgement form' : 'Continue pre-lodgement form', to: '/intake' },
      secondaryCta: { label: 'Upload documents', to: '/documents' },
      statusChip: completion > 0
        ? { tone: 'bg-[#FB8E28]/10 text-[#D97706]', label: 'In progress', icon: <Clock size={12} /> }
        : null,
    };
  }

  // Intake complete (or near it). Push docs.
  const docsRemaining = Math.max(0, requiredDocCount - docCount);
  if (docsRemaining > 0) {
    return {
      eyebrow: 'Your next step',
      heading: `Upload your remaining documents (${docsRemaining} of ${requiredDocCount})`,
      description:
        'Your intake form is done. Now upload the documents on your checklist so we can prepare your application.',
      showProgress: false,
      primaryCta: { label: 'Upload documents', to: '/documents' },
      secondaryCta: { label: 'Review intake', to: '/intake' },
      statusChip: { tone: 'bg-[#FB8E28]/10 text-[#D97706]', label: `${docCount}/${requiredDocCount}`, icon: <Clock size={12} /> },
    };
  }

  if (intakeStatus === 'submitted' || intakeStatus === 'completed') {
    return {
      eyebrow: 'Submitted',
      heading: 'Your case manager is reviewing your application',
      description:
        "We'll be in touch within 24 to 48 business hours if anything's missing. You can always come back to upload more or message your case manager.",
      showProgress: false,
      primaryCta: { label: 'View documents', to: '/documents' },
      secondaryCta: { label: 'Review intake', to: '/intake' },
      statusChip: { tone: 'bg-green-100 text-green-700', label: 'Submitted', icon: <CheckCircle2 size={12} /> },
    };
  }

  // Fallback (shouldn't usually hit).
  return {
    eyebrow: 'Your next step',
    heading: 'Complete your pre-lodgement form',
    description: 'About 45 to 90 minutes. Save as you go.',
    showProgress: true,
    primaryCta: { label: 'Open pre-lodgement form', to: '/intake' },
    secondaryCta: { label: 'Upload documents', to: '/documents' },
    statusChip: null,
  };
}

function visaWelcomeBlurb(visaSubclass: string, variant: string | null): string {
  if (visaSubclass === '820' || visaSubclass === '801') {
    return `We're handling your ${visaSubclass}${variant ? ` ${variant}` : ''} partner visa application.`;
  }
  if (visaSubclass === '309' || visaSubclass === '100') {
    return `We're handling your ${visaSubclass}${variant ? ` ${variant}` : ''} offshore partner visa application.`;
  }
  if (visaSubclass === '300') return "We're handling your 300 Prospective Marriage Visa application.";
  if (visaSubclass === '461') return "We're handling your 461 NZ Family Relationship Visa application.";
  if (visaSubclass === '600-tourist') return "We're handling your 600 Tourist visa application.";
  if (visaSubclass === '600-family') return "We're handling your 600 Family Sponsored visa application.";
  return `We're handling your ${visaSubclass} visa application.`;
}

/**
 * Completion % from the schema's required-field coverage — the same
 * computeOwnFieldProgress the in-form header uses, so the dashboard number
 * matches what the client sees inside the form (counts only the client's own
 * required fields, excluding read-only cross-form context). Replaces the old
 * heuristic that split field keys on the first dot against a hardcoded section
 * count, which collapsed to ~1-2 "sections" under v2's applicant-dot /
 * sponsor-dot keys.
 */
async function computeCompletion(
  visaSubclass: string,
  isSponsor: boolean,
  variant: string | null,
  fields: Array<{ field_key: string; field_value: unknown }>,
  stage?: 1 | 2,
): Promise<number> {
  if (fields.length === 0) return 0;
  try {
    // null when this visa has no sponsor form (461 / 020 / 600-tourist).
    const formId = pickFormId(visaSubclass, isSponsor, variant);
    if (!formId) return 0;
    const full = await loadSchema(formId);
    // Scope to a single stage (pre/post-lodgement) when requested; client_hidden
    // sections never count toward the client-facing number.
    const schema = stage
      ? { ...full, sections: full.sections.filter((s) => ((s as { stage?: number }).stage ?? 1) === stage && !(s as { client_hidden?: boolean }).client_hidden) }
      : full;
    // Shared with the intake-form header (computeOwnFieldProgress): counts only
    // the client's own required fields, excluding read-only cross-form context,
    // so the dashboard number and the in-form header can't disagree (finding 8).
    return computeOwnFieldProgress(schema, rowsToFormState(fields));
  } catch {
    return 0;
  }
}

function formatDate(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch {
    return iso;
  }
}

function formatReceiptLine(r: VerifiedReceipt): string {
  // Manual override wins if set; otherwise use AI extraction.
  const date = r.manual_override?.date ?? r.ai_extracted_date ?? null;
  const amount = r.manual_override?.amount ?? r.ai_extracted_amount ?? null;
  const currency = r.manual_override?.currency ?? r.ai_extracted_currency ?? 'AUD';
  const method = r.manual_override?.method ?? r.ai_extracted_method ?? null;
  const amountStr = amount != null
    ? new Intl.NumberFormat('en-AU', { style: 'currency', currency }).format(amount)
    : 'Amount unrecorded';
  const dateStr = date ? formatDate(date) : 'date unrecorded';
  const methodLabel = method ? methodToLabel(method) : null;
  return methodLabel
    ? `${amountStr} received on ${dateStr} · ${methodLabel}`
    : `${amountStr} received on ${dateStr}`;
}

function methodToLabel(method: string): string {
  switch (method) {
    case 'card':
      return 'Card';
    case 'bank_transfer':
      return 'Bank transfer';
    case 'cash':
      return 'Cash';
    case 'cheque':
      return 'Cheque';
    case 'other':
      return 'Other';
    default:
      return method;
  }
}
